import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
} from '@mui/material'
import Box from '@mui/material/Box'
import Select from '@mui/material/Select'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {green, grey} from '@mui/material/colors'
import {useAuthProvider} from 'app/modules/auth'
import React from 'react'
import {useStylesCommon} from 'styles/useStyles'
import DividerWithTextCust from '../DividerWithTextCust'
import MultipleSelectChip from '../multiselect/MultipleSelectChip'
import {IFilterControlOptions} from './CFilters'
type Anchor = 'top' | 'left' | 'bottom' | 'right'
interface Props {
  entity: string
  subEntity?: string
  title: string
  filterControlOptions?: IFilterControlOptions[]
  parentSaveFilters?: (filters: IFilterControlOptions[], filterType: string) => void
  parentResetFilters?: (filters: IFilterControlOptions[], entity_type: string) => void
  toggleDrawerShut: (event: React.KeyboardEvent | React.MouseEvent | any) => void
  additional_data?: any
}

export default function FilterPanel({
  entity,
  subEntity,
  title,
  filterControlOptions,
  parentSaveFilters,
  parentResetFilters,
  toggleDrawerShut,
  additional_data,
}: Props) {
  const {reduxUser} = useAuthProvider()
  const classes = useStylesCommon()
  const profileEntityType = entity + `${subEntity ? '|' + subEntity : ''}`
  const [filtersControlOptionState, setFiltersControlOptionState] = React.useState<
    IFilterControlOptions[]
  >(
    reduxUser?.profile?.filters?.find((f) => f.type === profileEntityType)?.filters ??
      filterControlOptions ??
      []
  )

  //university data is in additional data
  const [universityValues, setUniversityValues] = React.useState<{key: any; value: any}[]>(
    additional_data?.values?.filter((f: any) =>
      reduxUser?.profile?.filters
        ?.find((t) => t.type === profileEntityType)
        ?.filters?.find((f) => f.fieldName === 'multi_university')
        ?.fieldValue?.includes(f.key)
    ) ?? []
  )

  const formatter = new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'})
  //used if have slider label
  const ValueIdentity = (n: number) => {
    return formatter.format(n)
  }
  const operatorKindLabel = (val: string) => {
    switch (val) {
      case '>': {
        return 'greater than'
      }
      case '>=': {
        return 'greater than or equal to'
      }
      case '<': {
        return 'less than'
      }
      case '<=': {
        return 'less than or equal to'
      }
      default: {
        return ''
      }
    }
  }

  const resetFilters = () => {
    const resetOptions = filtersControlOptionState.map((f) => {
      const n = {...f}
      delete n.fieldValue
      return n
    })
    setFiltersControlOptionState(resetOptions)
    parentResetFilters && parentResetFilters(resetOptions, entity)
    toggleDrawerShut(false)
  }
  const keepApplyFilters = () => {
    const newFilters = filtersControlOptionState.map((f) => {
      const n = {...f}
      return n
    })
    //check if any valid options
    if (profileEntityType.includes('innovation')) {
      const found = newFilters.find((f) => f.fieldName === 'multi_university')
      if (found) {
        found.fieldValue = universityValues?.map((m) => m.key)
      }
    }
    //callbacks
    parentSaveFilters && parentSaveFilters(newFilters, profileEntityType)
    toggleDrawerShut(false)
  }

  const exchangeValueRedraw = (
    fieldName: string,
    fieldVal: string | number | boolean | undefined | any,
    isOperator?: boolean
  ) => {
    if (isOperator) {
      const filts = filtersControlOptionState.map((f) => {
        const n = {...f}
        if (`${f.fieldName}_operator` === fieldName && typeof fieldVal === 'string') {
          n.fieldOperator = fieldVal
        }
        return n
      })
      setFiltersControlOptionState(filts)
    } else {
      const t =
        filtersControlOptionState?.map((f) => {
          const n = {...f}
          if (f.fieldName === fieldName || f.fieldName + f.wildcard === fieldName) {
            n.fieldValue = fieldVal
          }
          return n
        }) ?? []
      setFiltersControlOptionState(t)
    }
  }

  const filterOptions = React.useMemo(() => {
    return (
      <Box m={5} display='flex' flexDirection='column'>
        <Typography color={grey[800]} variant={'h5'} mb={4}>
          {`${title}`}
        </Typography>
        <Grid container>
          {filtersControlOptionState?.map((pf, i) => {
            return (
              <Box display={'contents'} key={`dyn_input${i}`}>
                {pf.type === 'textbox' ? (
                  //we do a textbox
                  <Grid item xs={6}>
                    <Box m={1}>
                      <TextField
                        fullWidth
                        name={`${pf.fieldName}${pf.wildcard ? pf.wildcard : ''}`}
                        variant='outlined'
                        autoCapitalize='off'
                        value={pf.fieldValue ?? pf.fieldDefaultValue ?? ''}
                        label={pf.fieldDisplay}
                        InputProps={{
                          endAdornment: pf.fieldValue && (
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => exchangeValueRedraw(pf.fieldName, '')}
                            >
                              <CancelRoundedIcon />
                            </IconButton>
                          ),
                        }}
                        onChange={(event) =>
                          exchangeValueRedraw(event.target.name, event.target.value)
                        }
                        // helperText={
                        //   <span className={classes.validationTextError}>
                        //     {Object.entries(errors)?.map(([k, v]) => {
                        //       if (k === pf.fieldName) return <>{v?.message?.toString()}</>
                        //     })}
                        //   </span>
                        // }
                        // {...register(`${pf.fieldName}`, {
                        //   onChange: (event) =>
                        //     exchangeValueRedraw(event.target.name, event.target.value),
                        // })}
                      />
                    </Box>
                  </Grid>
                ) : pf.type === 'selectbox' ? (
                  <Grid item xs={6}>
                    <Box m={1}>
                      <FormControl fullWidth>
                        <InputLabel id={`${pf.fieldName}-label_${i}`}>{pf.fieldDisplay}</InputLabel>
                        <Box display={'flex'} justifyContent={'space-between'}>
                          <Select
                            labelId={`${pf.fieldName}-label_${i}`}
                            id={`${pf.fieldName}_${i}`}
                            name={`${pf.fieldName}`}
                            value={pf.fieldValue ?? pf.fieldDefaultValue ?? ''}
                            label={pf.fieldDisplay}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value)
                            }
                            fullWidth
                          >
                            {pf.options?.map((s, i) => {
                              return (
                                <MenuItem key={s.key} value={s.key}>
                                  {s.value}
                                </MenuItem>
                              )
                            })}
                          </Select>
                          {pf.fieldValue && (
                            <IconButton
                              disableRipple
                              aria-label='toggle password visibility'
                              onClick={() => exchangeValueRedraw(pf.fieldName, '')}
                            >
                              <CancelRoundedIcon />
                            </IconButton>
                          )}
                        </Box>
                      </FormControl>
                    </Box>
                  </Grid>
                ) : pf.type === 'multichip' ? (
                  <Grid item xs={6}>
                    <Box m={1}>
                      <FormControl fullWidth>
                        {/* <InputLabel id={`${pf.fieldName}-label_${i}`}>{pf.fieldDisplay}</InputLabel> */}
                        <Box display={'flex'} justifyContent={'space-between'}>
                          <MultipleSelectChip
                            label={pf.fieldDisplay}
                            entities={pf.options ?? []}
                            values={universityValues}
                            setValues={setUniversityValues}
                          />
                        </Box>
                      </FormControl>
                    </Box>
                  </Grid>
                ) : pf.type === 'checkbox' ? (
                  <Grid item xs={6}>
                    <Box m={1}>
                      {pf.fieldName === 'awards' ? (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={`${pf.fieldName}`}
                              checked={
                                Boolean(
                                  filtersControlOptionState?.find(
                                    (f) => f.fieldName === pf.fieldName && pf.fieldValue
                                  )
                                ) ?? false
                              }
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                exchangeValueRedraw(
                                  event.target.name,
                                  event.target.checked ? {'id@': ''} : undefined
                                )
                              }
                            />
                          }
                          label={
                            <Typography variant={'h6'} color={grey[600]}>
                              {pf.fieldDisplay}
                            </Typography>
                          }
                        />
                      ) : (
                        <FormControlLabel
                          control={
                            <Checkbox
                              name={`${pf.fieldName}`}
                              checked={
                                Boolean(
                                  filtersControlOptionState?.find(
                                    (f) => f.fieldName === pf.fieldName && pf.fieldValue
                                  )
                                ) ?? false
                              }
                              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                exchangeValueRedraw(event.target.name, event.target.checked)
                              }
                            />
                          }
                          label={
                            <Typography variant={'h6'} color={grey[600]}>
                              {pf.fieldDisplay}
                            </Typography>
                          }
                        />
                      )}
                    </Box>
                  </Grid>
                ) : pf.type === 'range' ? (
                  <Grid item xs={6}>
                    <Box m={1} display={'flex'}>
                      <Grid
                        container
                        sx={{
                          boxShadow:
                            'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                          padding: '15px',
                        }}
                      >
                        <Grid item xs={12}>
                          <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography variant={'body1'} color={grey[700]}>
                              {pf.fieldDisplay}
                            </Typography>
                            {pf.fieldValue && (
                              <IconButton
                                disableRipple
                                onClick={() => {
                                  exchangeValueRedraw(`${pf.fieldName}_operator`, '', true)
                                  exchangeValueRedraw(pf.fieldName, '')
                                }}
                              >
                                <CancelRoundedIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <Select
                            variant={'filled'}
                            size='small'
                            id={`${pf.fieldName}_operator${i}`}
                            name={`${pf.fieldName}_operator`}
                            value={pf.fieldOperator ?? ''}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value, true)
                            }
                            fullWidth
                          >
                            <MenuItem key={'>'} value={'>'}>
                              {'> Greater Than'}
                            </MenuItem>
                            <MenuItem key={'<'} value={'<'}>
                              {'< Less Than'}
                            </MenuItem>
                            <MenuItem key={'>='} value={'>='}>
                              {'>= Greater Than or equal to'}
                            </MenuItem>
                            <MenuItem key={'<='} value={'<='}>
                              {'<= Less Than or equal to'}
                            </MenuItem>
                          </Select>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <Select
                            size='small'
                            variant={'filled'}
                            id={`${pf.fieldName}_${i}`}
                            name={`${pf.fieldName}`}
                            value={pf.fieldValue ?? pf.fieldDefaultValue ?? ''}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value)
                            }
                            fullWidth
                          >
                            {pf.options?.map((s, i) => {
                              return (
                                <MenuItem key={s.key} value={s.key}>
                                  {typeof s.value === 'object'
                                    ? Object.entries(s.value)[0].values.toString()
                                    : s.value}
                                </MenuItem>
                              )
                            })}
                          </Select>
                        </Grid>
                        {filtersControlOptionState.find(
                          (f) => f.fieldName === pf.fieldName && f.fieldValue
                        ) && (
                          <Grid item xs={12}>
                            <Typography variant={'body2'} color={green[600]}>
                              <CheckCircleIcon color={'success'} />
                              {`${pf.fieldDisplay} ${operatorKindLabel(
                                filtersControlOptionState.find((f) => f.fieldName === pf.fieldName)
                                  ?.fieldOperator ?? ''
                              )} ${formatter.format(
                                parseInt(
                                  filtersControlOptionState
                                    .find((f) => f.fieldName === pf.fieldName)
                                    ?.fieldValue?.toString() ?? ''
                                )
                              )}`}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                ) : pf.type === 'divider' ? (
                  <Grid item xs={12}>
                    <DividerWithTextCust
                      width={1}
                      colorDivider={grey[800]}
                      left={4}
                      right={4}
                      padLeft={'1em'}
                      padRight={'1em'}
                      weight={400}
                      colorText={grey[900]}
                      fs={12}
                    >
                      {pf.fieldDisplay}
                    </DividerWithTextCust>
                  </Grid>
                ) : pf.type === 'dategtlt' ? (
                  <Grid item xs={6}>
                    <Box m={1} display={'flex'}>
                      <Grid
                        container
                        sx={{
                          boxShadow:
                            'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                          padding: '15px',
                        }}
                      >
                        <Grid item xs={12}>
                          <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography variant={'body1'} color={grey[700]}>
                              {pf.fieldDisplay}
                            </Typography>
                            {pf.fieldValue && (
                              <IconButton
                                disableRipple
                                onClick={() => {
                                  exchangeValueRedraw(`${pf.fieldName}_operator`, '', true)
                                  exchangeValueRedraw(pf.fieldName, '')
                                }}
                              >
                                <CancelRoundedIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <Select
                            variant={'filled'}
                            size='small'
                            id={`${pf.fieldName}_operator${i}`}
                            name={`${pf.fieldName}_operator`}
                            value={pf.fieldOperator ?? ''}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value, true)
                            }
                            fullWidth
                          >
                            <MenuItem key={'>'} value={'>'}>
                              {'> Greater Than'}
                            </MenuItem>
                            <MenuItem key={'<'} value={'<'}>
                              {'< Less Than'}
                            </MenuItem>
                            <MenuItem key={'>='} value={'>='}>
                              {'>= Greater Than or equal to'}
                            </MenuItem>
                            <MenuItem key={'<='} value={'<='}>
                              {'<= Less Than or equal to'}
                            </MenuItem>
                          </Select>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <TextField
                            fullWidth
                            variant={'outlined'}
                            type={'date'}
                            name={`${pf.fieldName}${pf.wildcard ? pf.wildcard : ''}`}
                            value={pf.fieldValue ?? pf.fieldDefaultValue ?? ''}
                            InputProps={{
                              endAdornment: pf.fieldValue && (
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={() => exchangeValueRedraw(pf.fieldName, '')}
                                >
                                  <CancelRoundedIcon fontSize={'small'} />
                                </IconButton>
                              ),
                            }}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value)
                            }
                            sx={{margin: 0, padding: 0, '& input': {padding: '12px'}}}
                          />
                        </Grid>
                        {filtersControlOptionState.find(
                          (f) => f.fieldName === pf.fieldName && f.fieldValue
                        ) && (
                          <Grid item xs={12}>
                            <Typography variant={'body2'} color={green[600]}>
                              <CheckCircleIcon color={'success'} />
                              {`${pf.fieldDisplay} ${operatorKindLabel(
                                filtersControlOptionState.find((f) => f.fieldName === pf.fieldName)
                                  ?.fieldOperator ?? ''
                              )} ${
                                filtersControlOptionState
                                  .find((f) => f.fieldName === pf.fieldName)
                                  ?.fieldValue?.toString() ?? ''
                              }`}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                ) : pf.type === 'dategtltNoEqTo' ? (
                  <Grid item xs={6}>
                    <Box m={1} display={'flex'}>
                      <Grid
                        container
                        sx={{
                          boxShadow:
                            'rgba(50, 50, 93, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px',
                          padding: '15px',
                        }}
                      >
                        <Grid item xs={12}>
                          <Box display={'flex'} justifyContent={'space-between'}>
                            <Typography variant={'body1'} color={grey[700]}>
                              {pf.fieldDisplay}
                            </Typography>
                            {pf.fieldValue && (
                              <IconButton
                                disableRipple
                                onClick={() => {
                                  exchangeValueRedraw(`${pf.fieldName}_operator`, '', true)
                                  exchangeValueRedraw(pf.fieldName, '')
                                }}
                              >
                                <CancelRoundedIcon />
                              </IconButton>
                            )}
                          </Box>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <Select
                            variant={'filled'}
                            size='small'
                            id={`${pf.fieldName}_operator${i}`}
                            name={`${pf.fieldName}_operator`}
                            value={pf.fieldOperator ?? ''}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value, true)
                            }
                            fullWidth
                          >
                            <MenuItem key={'>'} value={'>'}>
                              {'> Greater Than'}
                            </MenuItem>
                            <MenuItem key={'<'} value={'<'}>
                              {'< Less Than'}
                            </MenuItem>
                          </Select>
                        </Grid>
                        <Grid item xs={6} padding={0.25}>
                          <TextField
                            fullWidth
                            variant={'outlined'}
                            type={'date'}
                            name={`${pf.fieldName}${pf.wildcard ? pf.wildcard : ''}`}
                            value={pf.fieldValue ?? pf.fieldDefaultValue ?? ''}
                            InputProps={{
                              endAdornment: pf.fieldValue && (
                                <IconButton
                                  aria-label='toggle password visibility'
                                  onClick={() => exchangeValueRedraw(pf.fieldName, '')}
                                >
                                  <CancelRoundedIcon fontSize={'small'} />
                                </IconButton>
                              ),
                            }}
                            onChange={(event) =>
                              exchangeValueRedraw(event.target.name, event.target.value)
                            }
                            sx={{margin: 0, padding: 0, '& input': {padding: '12px'}}}
                          />
                        </Grid>
                        {filtersControlOptionState.find(
                          (f) => f.fieldName === pf.fieldName && f.fieldValue
                        ) && (
                          <Grid item xs={12}>
                            <Typography variant={'body2'} color={green[600]}>
                              <CheckCircleIcon color={'success'} />
                              {`${pf.fieldDisplay} ${operatorKindLabel(
                                filtersControlOptionState.find((f) => f.fieldName === pf.fieldName)
                                  ?.fieldOperator ?? ''
                              )} ${
                                filtersControlOptionState
                                  .find((f) => f.fieldName === pf.fieldName)
                                  ?.fieldValue?.toString() ?? ''
                              }`}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Box>
                  </Grid>
                ) : (
                  <></>
                )}
              </Box>
            )
          })}
        </Grid>

        <Box m={1}>
          <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
            <Button variant={'outlined'} disabled={false} onClick={resetFilters}>
              Reset
            </Button>
            <Button variant={'contained'} disabled={false} onClick={keepApplyFilters}>
              Save Filters
            </Button>

            {/* <Button variant='contained' color='primary' type='submit'>
            Submit
          </Button> */}
          </Box>
        </Box>
      </Box>
    )
  }, [filtersControlOptionState, universityValues, reduxUser])

  return <>{filterOptions}</>
}

{
  /* <Slider
                  aria-label={pf.fieldName}
                  defaultValue={parseInt(pf.fieldDefaultValue ?? '') ?? 0}
                  valueLabelFormat={(value) => ValueIdentity(value)}
                  valueLabelDisplay='auto'
                  min={pf.min}
                  max={pf.max}
                /> */
}
